import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { ConfirmationModal } from './ui/Notification';
import { 
  FiGrid, FiUsers, FiInbox, FiActivity, FiSettings, FiLogOut, 
  FiShield, FiLock, FiChevronRight, FiZoomIn, FiZoomOut, FiRefreshCw
} from 'react-icons/fi';

export default function AdminLayout() {
  const { adminUser, adminLogout } = useAuth();
  const navigate = useNavigate();
  const [logoutModal, setLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);

  const zoomLevels = [85, 90, 95, 100, 105, 110, 115];
  
  useEffect(() => {
    // Load saved zoom level
    const savedZoom = localStorage.getItem('bpa_app_zoom_level');
    if (savedZoom && zoomLevels.includes(parseInt(savedZoom))) {
      setZoomLevel(parseInt(savedZoom));
    }
  }, []);

  useEffect(() => {
    // Apply zoom level to document root
    document.documentElement.style.setProperty('--app-zoom', zoomLevel / 100);
    localStorage.setItem('bpa_app_zoom_level', zoomLevel);
  }, [zoomLevel]);

  const handleZoomIn = () => {
    const currentIndex = zoomLevels.indexOf(zoomLevel);
    if (currentIndex < zoomLevels.length - 1) {
      setZoomLevel(zoomLevels[currentIndex + 1]);
    }
  };

  const handleZoomOut = () => {
    const currentIndex = zoomLevels.indexOf(zoomLevel);
    if (currentIndex > 0) {
      setZoomLevel(zoomLevels[currentIndex - 1]);
    }
  };

  const handleZoomReset = () => {
    setZoomLevel(100);
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try { 
      await adminApi.logout(adminUser.admin_session_token); 
    } catch (e) {
      console.warn("Admin logout sync failed", e);
    }
    adminLogout();
    navigate('/admin/login');
  };

  const navItems = [
    { to: '/admin', icon: FiGrid, label: 'Dashboard', end: true },
    { to: '/admin/clients', icon: FiUsers, label: 'Clients' },
    { to: '/admin/leads', icon: FiInbox, label: 'All Leads' },
    { to: '/admin/logs', icon: FiActivity, label: 'System Logs' },
    { to: '/admin/settings', icon: FiSettings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      <ConfirmationModal 
        isOpen={logoutModal} 
        title="Sign Out?" 
        message="Your administrative session will be ended securely." 
        confirmText="Sign Out" 
        confirmVariant="danger" 
        loading={loggingOut} 
        onConfirm={handleLogout} 
        onCancel={() => setLogoutModal(false)} 
      />

      {/* Sidebar */}
      <aside className="w-72 bg-surface flex flex-col border-r border-border flex-shrink-0">
        <div className="p-8 border-b border-border">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gradient-to-tr from-primary to-accent rounded-xl flex items-center justify-center text-white shadow-card">
              <span className="font-bold text-lg tracking-tight">BPA</span>
            </div>
            <div>
              <h1 className="font-bold text-base text-ink tracking-tight">BPA Admin Console</h1>
              <p className="text-xs text-muted">Lead Management</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4">
          <div className="space-y-1">
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-premium
                  ${isActive 
                    ? 'bg-gradient-to-r from-primary to-accent text-white shadow-premium' 
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
                `}
              >
                <SafeIcon icon={item.icon} className="text-base" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>

        <div className="p-4 border-t border-border">
          <div className="bg-slate-50 rounded-lg p-3 border border-border mb-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-primary shadow-sm border border-border">
                <SafeIcon icon={FiShield} />
              </div>
              <div className="overflow-hidden">
                <p className="text-xs font-medium text-ink truncate">{adminUser?.email?.split('@')[0]}</p>
                <p className="text-[11px] text-muted">Admin Access</p>
              </div>
            </div>
          </div>
          <button 
            onClick={() => setLogoutModal(true)} 
            className="w-full flex items-center gap-3 px-4 py-3 text-slate-600 hover:text-error hover:bg-red-50 rounded-lg font-medium transition-premium"
          >
            <SafeIcon icon={FiLogOut} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {/* Top Header Bar */}
        <div className="sticky top-0 z-30 bg-surface/60 backdrop-blur border-b border-border px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/5 text-primary rounded-lg border border-primary/10">
              <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse" />
              <span className="text-xs font-medium">Secure Admin</span>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-xs text-muted">
              <SafeIcon icon={FiLock} className="text-xs" />
              <span>Encrypted Session</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Zoom Controls */}
            <div className="flex items-center gap-1 bg-slate-50 p-1 rounded-lg border border-border">
              <button 
                onClick={handleZoomOut}
                disabled={zoomLevel === zoomLevels[0]}
                className="p-1.5 text-slate-600 hover:text-primary hover:bg-white rounded-md transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                title="Zoom out"
              >
                <SafeIcon icon={FiZoomOut} />
              </button>
              <span className="px-2 text-xs font-medium text-slate-700 min-w-[40px] text-center">
                {zoomLevel}%
              </span>
              <button 
                onClick={handleZoomIn}
                disabled={zoomLevel === zoomLevels[zoomLevels.length - 1]}
                className="p-1.5 text-slate-600 hover:text-primary hover:bg-white rounded-md transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                title="Zoom in"
              >
                <SafeIcon icon={FiZoomIn} />
              </button>
              {zoomLevel !== 100 && (
                <button 
                  onClick={handleZoomReset}
                  className="p-1.5 text-slate-600 hover:text-primary hover:bg-white rounded-md transition-all"
                  title="Reset zoom"
                >
                  <SafeIcon icon={FiRefreshCw} />
                </button>
              )}
            </div>
            
            <button className="p-2 text-muted hover:text-slate-900 transition-colors">
              <SafeIcon icon={FiLock} />
            </button>
            <div className="w-8 h-8 rounded-full bg-slate-50 border border-border flex items-center justify-center text-xs font-medium text-slate-600">
              {adminUser?.email?.charAt(0).toUpperCase()}
            </div>
          </div>
        </div>

        <div className="max-w-content mx-auto p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}